/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import React from "react";
import { Else, If, Then, When } from "react-if";
import {
  ExtendedTooltip,
  FlexContainer,
  Heading,
  Spinner,
  SVGIcon,
  Text,
  theme,
} from "@nordcloud/gnui";

import { ResourceUpdateStatus } from "../../types";
import { getUpdateStatusDetails } from "../../utils";
import { CardContainer, StatusBorder } from "./CardContainer";

type Props = {
  title: string;
  count: number | string;
  status: ResourceUpdateStatus;
  onClick?: (status: ResourceUpdateStatus) => void;
  tooltip?: string;
  active?: boolean;
  loading?: boolean;
};

export function StatusCard({
  title,
  count,
  status,
  onClick,
  tooltip,
  active,
  loading,
}: Props) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!onClick) {
      return;
    }

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick(status);
    }
  };

  const { spinnerColor, color } = getUpdateStatusDetails(status);

  return (
    <CardContainer
      status={status}
      $clickable={Boolean(onClick)}
      $active={Boolean(active)}
      hideBorderTop={status === ResourceUpdateStatus.Total}
      onClick={() => onClick?.(status)}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={handleKeyDown}
    >
      <When condition={status !== ResourceUpdateStatus.Total}>
        <StatusBorder color={color} />
      </When>
      <FlexContainer
        alignItems={"center"}
        gap={theme.spacing.spacing02}
        marginBottom={theme.spacing.spacing01}
      >
        <Heading level={4} css={{ lineHeight: "normal" }}>
          {title}
        </Heading>
        <When condition={tooltip}>
          <ExtendedTooltip caption={tooltip}>
            <SVGIcon
              name="info"
              size="smd"
              css={{
                display: "block",
              }}
            />
          </ExtendedTooltip>
        </When>
      </FlexContainer>

      <If condition={loading}>
        <Then>
          <FlexContainer alignItems={"center"} css={{ height: "42px" }}>
            <Spinner size="md" color={spinnerColor} />
          </FlexContainer>
        </Then>
        <Else>
          <Text size="xxl" color={color} marginBottom={0} lineHeight="normal">
            {count}
          </Text>
        </Else>
      </If>
    </CardContainer>
  );
}
